import {Component, Inject, OnInit, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {AiFinisherService} from '../../service/ai-finisher.service';
import {AuthService} from '../../service/auth.service';
import {AiSuggestion, AiWorkResponse} from '../../models/ai-suggestion-model';
import {AiSuggestionDetailComponent} from '../ai-suggestion-detail/ai-suggestion-detail.component';

/** What the imports table passes when the engineer clicks a row's AI icon. */
export interface AiImportDialogData {
    importId: number;
    filename?: string;
}

/**
 * The imports-table AI popup: everything the AI did for ONE client import — the suggestion's
 * rationale + confidence + diff + decision log (the same detail pane as the AI Finisher cockpit),
 * with download / approve / reject right here. Multiple suggestions (rare) are switchable.
 */
@Component({
    selector: 'app-ai-import-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, AiSuggestionDetailComponent],
    templateUrl: './ai-import-dialog.component.html',
    styleUrl: './ai-import-dialog.component.css'
})
export class AiImportDialogComponent implements OnInit {

    private api = inject(AiFinisherService);
    private toastr = inject(ToastrService);
    private auth = inject(AuthService);

    suggestions = signal<AiSuggestion[]>([]);
    selected = signal<AiSuggestion | null>(null);
    aiWork = signal<AiWorkResponse | null>(null);
    loading = signal<boolean>(true);
    busy = signal<boolean>(false);

    constructor(public dialogRef: MatDialogRef<AiImportDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public data: AiImportDialogData) {
    }

    ngOnInit(): void {
        this.api.getSuggestionsForImport(this.data.importId).subscribe({
            next: (list) => {
                this.loading.set(false);
                this.suggestions.set(list ?? []);
                this.selected.set(list && list.length > 0 ? list[0] : null);
            },
            error: () => {
                this.loading.set(false);
                this.toastr.error('Could not load the AI suggestions for this import');
            }
        });
        this.api.getAiWork(this.data.importId).subscribe({
            next: (w) => this.aiWork.set(w),
            error: () => this.aiWork.set(null)   // 404 = no decision log — the detail pane copes
        });
    }

    select(s: AiSuggestion): void {
        this.selected.set(s);
    }

    approveSelected(): void {
        const s = this.selected();
        if (!s) {
            return;
        }
        this.busy.set(true);
        this.api.approve(s.id, this.approverName()).subscribe({
            next: (updated) => {
                this.busy.set(false);
                this.applyUpdate(updated ?? {...s, approvalStatus: 'APPROVED'});
                this.toastr.success('Suggestion approved — dcf-main-app will finalise it');
            },
            error: () => {
                this.busy.set(false);
                this.toastr.error('Approve failed');
            }
        });
    }

    rejectSelected(reason: string): void {
        const s = this.selected();
        if (!s) {
            return;
        }
        this.busy.set(true);
        this.api.reject(s.id, reason).subscribe({
            next: (updated) => {
                this.busy.set(false);
                this.applyUpdate(updated ?? {...s, approvalStatus: 'REJECTED', rejectReason: reason});
                this.toastr.success('Suggestion rejected');
            },
            error: () => {
                this.busy.set(false);
                this.toastr.error('Reject failed');
            }
        });
    }

    downloadSelected(): void {
        const s = this.selected();
        if (!s) {
            return;
        }
        this.busy.set(true);
        this.api.downloadBundle(s.id).subscribe({
            next: (resp) => {
                this.busy.set(false);
                const body = resp.body;
                if (!body) {
                    this.toastr.error('Download was empty');
                    return;
                }
                const name = this.filenameFrom(resp.headers.get('content-disposition'))
                    ?? `${s.winOlsReference || 'ai-suggestion-' + s.id}.zip`;
                const url = URL.createObjectURL(body);
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                a.click();
                URL.revokeObjectURL(url);
            },
            error: () => {
                this.busy.set(false);
                this.toastr.error('Download failed');
            }
        });
    }

    private applyUpdate(updated: AiSuggestion): void {
        this.suggestions.update(list => list.map(x => x.id === updated.id ? {...x, ...updated} : x));
        this.selected.set(updated);
    }

    private approverName(): string | undefined {
        const u = this.auth.currentUser;
        if (!u) {
            return undefined;
        }
        const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
        return name || u.email || undefined;
    }

    private filenameFrom(contentDisposition: string | null): string | null {
        if (!contentDisposition) {
            return null;
        }
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition);
        return match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
    }
}
